import { buildAssetView } from 'src/models/content/build-asset-view.js';
import { buildContractView } from 'src/models/content/build-contract-view.js';
import { buildCompetenceView } from 'src/models/content/build-competence-view.js';
import { buildDocumentView } from 'src/models/content/build-document-view.js';
import { buildEmployeeView } from 'src/models/content/build-employee-view.js';
import { buildEventOccurrenceView } from 'src/models/content/build-event-occurrence-view.js';
import { buildFunctionView } from 'src/models/content/build-function-view.js';
import { buildGuidelineView } from 'src/models/content/build-guideline-view.js';
import { buildIssueView } from 'src/models/content/build-issue-view.js';
import { buildMeetingView } from 'src/models/content/build-meeting-view.js';
import { buildContactView, buildPartnerView } from 'src/models/content/build-partner-view.js';
import { buildReportView } from 'src/models/content/build-report-view.js';
import { buildRiskAssessmentView } from 'src/models/content/build-risk-assessment-view.js';
import { buildSubstanceView } from 'src/models/content/build-substance-view.js';
import { buildTaskView } from 'src/models/content/build-task-view.js';
import { buildTutorialView } from 'src/models/content/build-tutorial-view.js';
import type { State } from 'src/store/types.js';
import { notifyError } from 'src/utilities/monitoring.js';
import { buildMeetingOccurrenceView } from 'src/models/content/build-meeting-occurrence-view';
import { EntityNotFoundError } from 'src/models/content/entity-not-found';

import { convertInternalLinks } from 'src/models/internal-linking.js';
import { docsForLinking } from 'src/store/selectors/linking/docsForLinking';
import { docsForLinkingById } from 'src/store/selectors/linking/docsForLinkingById';
import type { ApiClient } from 'src/store/backend';
import {
  employeesNotTerminatedCount,
  getPeopleForAccessControl,
  getOrganization,
  getOrganizationId,
  isCurrentUserEmployee,
  sector,
  writeAccess,
} from 'src/store/selectors';
import { currentUserIsOwner } from 'src/store/selectors/employees/current-user-is-owner';
import { LocalDate } from 'src/utilities/local-date';
import { buildCollectionView } from 'src/models/content/build-collection-view';
import { storedCollections } from 'src/store/selectors/collections';
import type { ContentView } from '../../content/content-view';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import { getSpecialTerms } from '../../store/selectors/features/getSpecialTerms';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { buildScreenView } from './build-screen-view';
import { buildSummaryView } from './build-summary-view';
import { trace } from 'src/utilities/trace';
import { emergencyAccess } from 'src/store/selectors/emergency/emergency-access';

async function buildContentView(
  api: ApiClient,
  type: string,
  uuid: string,
  state: State,
  currentParent: string,
): Promise<ContentView> {
  return trace(`contentView.build.${type}`, () => buildContentViewInner(api, type, uuid, state, currentParent), {
    'content.type': type,
    'content.uuid': uuid,
  });
}

async function buildContentViewInner(
  api: ApiClient,
  type: string,
  uuid: string,
  state: State,
  currentParent: string,
): Promise<ContentView> {
  const commonData: CommonDataEntityView = {
    today: LocalDate.fromString(state.today),
    currentUserHasWriteAccess: writeAccess(state),
    currentUserHasEmergencyAccess: emergencyAccess(state),
    currentUserIsOwner: currentUserIsOwner(state),
    sector: sector(state),
    featureStates: getFeatureStates(state),
    specialTerms: getSpecialTerms(state),
    employeesCount: employeesNotTerminatedCount(state),
    currentUserIsEmployee: isCurrentUserEmployee(state),
    accessControlOptions: getPeopleForAccessControl(state).map((e) => ({
      value: e.id,
      text: e.value,
      disabled: e.disabled,
    })),
    availableCollections: storedCollections(state),
    organizationId: '' + getOrganizationId(state),
    organizationName: getOrganization(state)?.name ?? '',
    promoData: undefined,
    unchangedTemplate: false,
    pages: [],
  };
  switch (type) {
    case 'assets':
      return buildAssetView(api, uuid, commonData, state, currentParent);
    case 'constitutionalDocuments':
      return buildContractView(api, uuid, commonData, state, currentParent, 'constitutionalDocuments');
    case 'contracts':
      return buildContractView(api, uuid, commonData, state, currentParent, 'contracts');
    case 'competence':
      return buildCompetenceView(api, uuid, commonData, state, currentParent);
    case 'documents':
      return buildDocumentView(api, uuid, commonData, state, currentParent);
    case 'employees':
      return buildEmployeeView(api, uuid, commonData, state, currentParent);
    case 'eventOccurrences':
      return buildEventOccurrenceView(api, uuid, commonData, state, currentParent);
    case 'meetingOccurrences':
      return buildMeetingOccurrenceView(api, uuid, commonData, state, currentParent);
    case 'functions':
      return buildFunctionView(api, uuid, commonData, state, currentParent);
    case 'tasks':
      return buildTaskView(api, uuid, commonData, state, currentParent);
    case 'guidelines':
      return buildGuidelineView(api, uuid, commonData, state, currentParent);
    case 'issues':
      return buildIssueView(api, uuid, commonData, state, currentParent);
    case 'meetings':
      return buildMeetingView(api, uuid, commonData, state, currentParent);
    case 'partners':
      return buildPartnerView(api, uuid, commonData, state, currentParent);
    case 'contacts':
      return buildContactView(api, uuid, commonData, state, currentParent);
    case 'reports':
      return buildReportView(api, uuid, commonData, state, currentParent);
    case 'riskAssessments':
      return buildRiskAssessmentView(api, uuid, commonData, state, currentParent);
    case 'substances':
      return buildSubstanceView(api, uuid, commonData, state, currentParent);
    case 'tutorials':
      return buildTutorialView(api, uuid, commonData, state, currentParent);
    case 'screens':
      return buildScreenView(api, uuid, commonData, state, currentParent);
    case 'collections':
      return buildCollectionView(api, uuid, commonData, state, currentParent);
    case 'summaries':
      return buildSummaryView(api, uuid, commonData, state, currentParent);
    default:
      throw new Error('Illegal state (E180), unexpected document type ' + type);
  }
}

export async function currentContentViews(
  api: ApiClient,
  hrefPrefix: string,
  pathSegments: string[],
  state: State,
): Promise<ContentView[]> {
  return trace('contentView.current', () => currentContentViewsInner(api, hrefPrefix, pathSegments, state), {
    'path.segments': pathSegments.join('/'),
  });
}

async function currentContentViewsInner(
  api: ApiClient,
  hrefPrefix: string,
  pathSegments: string[],
  state: State,
): Promise<ContentView[]> {
  let p = pathSegments.slice(1);
  let currentParent = hrefPrefix.substring(0, hrefPrefix.length - 1);
  const result: ContentView[] = [];
  try {
    while (p.length > 0) {
      const type = p[0];
      const uuid = p[1];
      p = p.slice(2);
      try {
        const contentView = await buildContentView(api, type, uuid, state, currentParent);
        const docsIndex = docsForLinkingById(state);
        if ('procedures' in contentView) {
          contentView.procedures = convertInternalLinks(contentView.procedures, docsIndex);
        }
        if ('relatedMeetingProcedures' in contentView) {
          contentView.relatedMeetingProcedures = convertInternalLinks(contentView.relatedMeetingProcedures, docsIndex);
        }
        if ('description' in contentView) {
          contentView.description = convertInternalLinks(contentView.description, docsIndex);
        }
        if ('content' in contentView) {
          contentView.content = convertInternalLinks(contentView.content, docsIndex);
        }
        if ('meetingAgenda' in contentView) {
          contentView.meetingAgenda = convertInternalLinks(contentView.meetingAgenda, docsIndex);
        }
        if (contentView.type !== 'empty') {
          contentView.docsForLinking = docsForLinking(state);
        }
        result.push(contentView);
      } catch (e1) {
        // Entity missing from store (deleted / wrong org / stale link) — expected,
        // not a bug. Report once as a grouped warning with a stable message (uuid in
        // metadata, not the message) so all not-found cases collapse to one issue,
        // then degrade to an empty view like any other build failure.
        if (e1 instanceof EntityNotFoundError) {
          console.warn(`Entity not found while building view model: ${type} ${uuid}`);
          notifyError(new Error('Content view: entity not found in store'), {
            severity: 'warning',
            context: 'Content View Factory: entity not found',
            metadata: { entity: { type, uuid, path: currentParent } },
          });
          result.push({
            type: 'empty',
            parentHref: currentParent,
            href: currentParent + '/' + type + '/' + uuid,
            currentUserHasAccess: true,
          });
          currentParent += '/' + type + '/' + uuid;
          continue;
        }

        // Extract HTTP status from ResponseError if available
        const responseError = e1 as any;
        const httpStatus = responseError?.status ?? responseError?.response?.status;
        const errorMessage = e1 instanceof Error ? e1.message : String(e1);
        const errorType = e1?.constructor?.name ?? 'unknown';

        console.error(`Failed to build view model for ${type} ${uuid}:`, {
          type,
          uuid,
          path: currentParent,
          httpStatus: httpStatus ?? 'unknown',
          errorType,
          errorMessage,
          error: e1,
        });

        // Enhanced error reporting with context
        notifyError(new Error(`Building content view model failed: ${errorMessage}`), {
          context: 'Content View Factory',
          metadata: {
            entity: {
              type: type,
              uuid: uuid,
              path: currentParent,
              error: errorMessage,
              errorType,
              httpStatus: httpStatus ?? 'unknown',
            },
            request: {
              currentParent,
              pathSegments: JSON.stringify(pathSegments),
            },
            // Add original error as metadata if available
            ...(httpStatus
              ? {
                  http: {
                    status: httpStatus,
                    statusText: responseError?.response?.statusText ?? 'unknown',
                  },
                }
              : {}),
          },
        });
        result.push({
          type: 'empty',
          parentHref: currentParent,
          href: currentParent + '/' + type + '/' + uuid,
          currentUserHasAccess: true,
        });
      }
      currentParent += '/' + type + '/' + uuid;
    }
  } catch (e) {
    console.error(e);
    notifyError(new Error('Building content view model ' + e));
  }

  return result;
}
